import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  StorageOutlined as DatabasesIcon,
  ScienceOutlined as SyntheticIcon,
  PeopleOutlined as UsersIcon,
  CallSplitOutlined as BranchIcon,
  SettingsOutlined as GeneralIcon,
  TuneOutlined as EnvironmentSettingsIcon,
  FolderOutlined as RepositoriesIcon,
  AppsOutlined as ServicesIcon,
  SecurityOutlined as SecurityIcon,
  PublicOutlined as DomainsIcon,
  SpeedOutlined as PerformanceIcon,
  PhoneIphoneOutlined as PhoneIcon,
} from '@mui/icons-material'
import { apiClient } from '../../utils/apiClient'
import { useOrganizationSettings } from '../../hooks/useOrganizationSettings'
import {
  getProjectNavSections,
  isProjectNavItemActive,
  type ProjectNavPage,
} from '../../utils/projectSettingsNav'
import { projectIosPath } from '../../utils/projectSettingsNav'
import type { IosEnvironment } from '../../utils/iosPhone'
import { NavItem, SectionLabel, ShowMorePill } from './sidebarPrimitives'
import BetaTag from '../BetaTag'

const ENVIRONMENTS_VISIBLE_COUNT = 5

type ProjectEnvironment = {
  id: number
  changeset_id?: string
  slug?: string
  env_slug?: string
  created_at?: string
}

/** One sidebar row: a regular preview environment or a mobile (iOS) one. */
type SidebarEnvironment = {
  key: string
  kind: 'web' | 'mobile'
  label: string
  title: string
  path: string
  /** Route param id for web environments, so deep pages keep the row active. */
  webId?: string
  createdAt: number
}

export default function ProjectContextNav({
  projectId,
  envId,
  onNavigate,
}: {
  projectId: string
  envId: string | null
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { telemetryEnabled, iosRunsEnabled } = useOrganizationSettings()
  const [environments, setEnvironments] = useState<ProjectEnvironment[]>([])
  const [iosEnvironments, setIosEnvironments] = useState<IosEnvironment[]>([])
  const [showAllEnvironments, setShowAllEnvironments] = useState(false)

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  useEffect(() => {
    setShowAllEnvironments(false)
    let cancelled = false
    const load = () => {
      apiClient
        .get<ProjectEnvironment[]>(`/api/projects/${projectId}/environments`)
        .then((data) => {
          if (!cancelled) setEnvironments(Array.isArray(data) ? data : [])
        })
        .catch(() => {
          if (!cancelled) setEnvironments([])
        })
      if (!iosRunsEnabled) {
        setIosEnvironments([])
        return
      }
      apiClient
        .get<IosEnvironment[]>(`/api/ios/environments?project_id=${projectId}`)
        .then((data) => {
          if (!cancelled) setIosEnvironments(Array.isArray(data) ? data : [])
        })
        .catch(() => {
          if (!cancelled) setIosEnvironments([])
        })
    }
    load()
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (String(detail?.projectId) === String(projectId)) load()
    }
    window.addEventListener('veilstream:project-environments-changed', handler)
    return () => {
      cancelled = true
      window.removeEventListener('veilstream:project-environments-changed', handler)
    }
  }, [projectId, iosRunsEnabled])

  const rows: SidebarEnvironment[] = [
    ...environments.map((env): SidebarEnvironment => {
      const label = env.changeset_id || env.env_slug || env.slug || `env-${env.id}`
      return {
        key: `web-${env.id}`,
        kind: 'web',
        label,
        title: label,
        path: `/project/${projectId}/environment/${env.id}`,
        webId: String(env.id),
        createdAt: Date.parse(env.created_at ?? '') || 0,
      }
    }),
    ...(iosRunsEnabled ? iosEnvironments.filter(env => !env.preview_environment_id) : []).map((env): SidebarEnvironment => {
      const label = env.name || env.slug
      return {
        key: `ios-${env.id}`,
        kind: 'mobile',
        label,
        title: `${label} (mobile)`,
        path: `${projectIosPath(projectId)}/${env.id}`,
        createdAt: Date.parse(env.created_at) || 0,
      }
    }),
  ].sort((x, y) => y.createdAt - x.createdAt)

  const isRowActive = (row: SidebarEnvironment) =>
    location.pathname === row.path ||
    location.pathname.startsWith(`${row.path}/`) ||
    (row.webId != null && envId != null && row.webId === String(envId))

  const activeIdx = rows.findIndex(isRowActive)
  const visibleRows =
    showAllEnvironments || activeIdx >= ENVIRONMENTS_VISIBLE_COUNT
      ? rows
      : rows.slice(0, ENVIRONMENTS_VISIBLE_COUNT)
  const hasMoreEnvironments = rows.length > ENVIRONMENTS_VISIBLE_COUNT
  const settingsNavIcon = (page: ProjectNavPage) => {
    switch (page) {
      case 'general':
        return <GeneralIcon />
      case 'environment-settings':
        return <EnvironmentSettingsIcon />
      case 'repositories':
        return <RepositoriesIcon />
      case 'databases':
        return <DatabasesIcon />
      case 'synthetic-databases':
        return <SyntheticIcon />
      case 'users':
        return <UsersIcon />
      case 'services':
        return <ServicesIcon />
      case 'security':
        return <SecurityIcon />
      case 'domains':
        return <DomainsIcon />
      case 'performance-analysis':
        return <PerformanceIcon />
      case 'ios':
        return <PhoneIcon />
      default:
        return <GeneralIcon />
    }
  }

  return (
    <>
      {getProjectNavSections(projectId, { telemetryEnabled }).map((section) => (
        <div key={section.id}>
          <SectionLabel label={section.label} tag={section.tag === 'beta' ? <BetaTag /> : undefined} />
          {section.items.map((item) => (
            <NavItem
              key={item.page}
              text={item.label}
              icon={settingsNavIcon(item.page)}
              active={isProjectNavItemActive(location.pathname, item.path)}
              onClick={() => go(item.path)}
              pl={1.5}
              fontSize="0.8125rem"
            />
          ))}
        </div>
      ))}

      {rows.length > 0 && (
        <>
          <SectionLabel label="Environments" />
          {visibleRows.map((row) => (
            <NavItem
              key={row.key}
              text={row.label}
              title={row.title}
              icon={row.kind === 'mobile' ? <PhoneIcon /> : <BranchIcon />}
              active={isRowActive(row)}
              onClick={() => go(row.path)}
              pl={1.5}
              fontSize="0.8125rem"
            />
          ))}
          {hasMoreEnvironments && (
            <ShowMorePill
              expanded={showAllEnvironments}
              hiddenCount={rows.length - ENVIRONMENTS_VISIBLE_COUNT}
              onClick={() => setShowAllEnvironments((v) => !v)}
            />
          )}
        </>
      )}
    </>
  )
}
